import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from 'reactstrap';
import styled from 'styled-components/macro';
import { theme } from 'styled-tools';
import { H5, P } from 'components/Typography';
import Truncate from 'react-truncate';
import { Link } from 'react-router-dom';
import dompurify from 'dompurify';
import BaseButtonWrapper from 'components/Button/ButtonWrapper';
import Button from 'components/Button';
import { CheckIcon } from 'components/Icons';
import { useForm, useFormState } from 'react-final-form';
import ModuleInfo from './ModuleInfo';

const ModuleListItem = styled(Row)`
  padding: 1rem;
  border-top: 1px solid ${theme('colors.lightGray')};
`;

const ModuleTitle = styled(H5)`
  cursor: pointer;
  color: ${theme('colors.primaryDark')};

  &:hover {
    text-decoration: underline;
  }
`;

const ModuleDescription = styled(P)`
  font-size: 0.75rem;
`;

const ModulePresenter = styled(Row)`
  font-size: 0.75rem;
`;

const Label = styled.label`
  color: ${theme('colors.darkerGray')};
`;

const ButtonWrapper = styled(BaseButtonWrapper)`
  justify-content: center;
`;

const ModuleAddedBadge = styled.span`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: ${theme('colors.primary')};

  ${CheckIcon} {
    margin-bottom: 0.25rem;
  }
`;

const StyledLink = styled(Link)`
  text-decoration: underline;
`;

function ListItem({ module, disabled = false }) {
  const form = useForm();
  const { values } = useFormState();

  const moduleIsAdded = values.modules && values.modules.find(m => m.module.id === module.id);

  const handleAddModule = () => {
    if (typeof values.modules === 'undefined') {
      values.modules = [];
    }

    if (values.modules && !moduleIsAdded) {
      form.mutators.push('modules', { includeQuiz: !!module?.quiz?.enabled, module: module });
    }
  };

  return (
    <ModuleListItem>
      <Col md={disabled ? 12 : 9}>
        <ModuleTitle
          onClick={() => {
            window.open(`/module/${module.id}`);
          }}
        >
          {module.title}
        </ModuleTitle>
        <ModuleDescription>
          <Truncate
            lines={2}
            ellipsis={
              <span>
                ...{' '}
                <StyledLink to={`/module/${module.id}`} target='_blank' aria-label={`View ${module.title}`}>
                  View More
                </StyledLink>
              </span>
            }
          >
            <span dangerouslySetInnerHTML={{ __html: dompurify.sanitize(module.description) }} />
          </Truncate>
        </ModuleDescription>
        {module.presenters && (
          <ModulePresenter>
            <Col>
              <Label>Presented by</Label>{' '}
              {module.presenters
                .map(presenter => (presenter.title ? presenter.name + ', ' + presenter.title : presenter.name))
                .join(', ')}
            </Col>
          </ModulePresenter>
        )}
        <ModuleInfo
          playingTimeInSeconds={module.playingTimeInSeconds}
          quiz={module.quiz}
          learningCredits={module.learningCredits}
          hasIsbeCompletionCertificate={module.hasIsbeCompletionCertificate}
        />
      </Col>
      {!disabled && (
        <Col md={3}>
          <ButtonWrapper align='center'>
            {!moduleIsAdded ? (
              <Button kind={'tertiary'} type='button' onClick={handleAddModule}>
                Add Module
              </Button>
            ) : (
              <ModuleAddedBadge>
                <CheckIcon size='2x' />
                <label>Module Added!</label>
              </ModuleAddedBadge>
            )}
          </ButtonWrapper>
        </Col>
      )}
    </ModuleListItem>
  );
}

ListItem.propTypes = {
  module: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    presenters: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        title: PropTypes.string,
      })
    ),
    playingTimeInSeconds: PropTypes.string,
    hasIsbeCompletionCertificate: PropTypes.string,
    learningCredits: PropTypes.string.isRequired,
    quiz: PropTypes.shape({
      enabled: PropTypes.bool,
    }),
  }).isRequired,
  disabled: PropTypes.bool,
};

export default ListItem;
